import { useState, useEffect, useMemo, useCallback } from "react";
import Header from "./header";
import { END_POINT } from "../../helper/helper";

// ─── DATA ──────────────────────────────────────────────────────────────────────


const PRICING_OPTIONS = [
  { value: "Free",     label: "Free",     count: 156 },
  { value: "Freemium", label: "Freemium", count: 248 },
  { value: "Paid",     label: "Paid",     count: 120 },
];

const FEATURE_OPTIONS = [
  { value: "API",       label: "API Available",      count: 198 },
  { value: "OpenSource",label: "Open Source",        count: 82  },
  { value: "Mobile",    label: "Mobile App",         count: 167 },
  { value: "Chrome",    label: "Chrome Extension",   count: 94  },
  { value: "NoCode",    label: "No Code Required",   count: 213 },
];

// ─── HELPERS ───────────────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function StarRating({ rating }) {
  return (
    <span className="stars">
      {"★".repeat(Math.floor(rating))}{"☆".repeat(5 - Math.floor(rating))}
    </span>
  );
}

// ─── VIDEO MODAL ───────────────────────────────────────────────────────────────
function VideoModal({ tool, onClose }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "auto";
    };
  }, [onClose]);

  return (
    <>
    <div className="video-modal active" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="video-modal-content">
        <div className="video-modal-header">
          <div className="video-modal-title">
            <span id="modalToolIcon">{tool.icon}</span>
            <span id="modalToolName">{tool.name}</span>
          </div>
          <button className="video-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="video-modal-body">
          <div className="video-container">
            {tool.videos?.youtube ? (
              <iframe
                src={tool.videos.youtube}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`${tool.name} demo`}
              />
            ) : (
              <div style={{ padding: "4rem", textAlign: "center", color: "var(--muted)" }}>
                <p>Video demo coming soon!</p>
              </div>
            )}
          </div>
          <div className="video-links">
            {tool.videos?.tutorial && (
              <a href={tool.videos.tutorial} target="_blank" rel="noreferrer" className="video-link-btn">
                <span className="video-link-icon">📺</span>
                <span>YouTube Tutorial</span>
              </a>
            )}
            {tool.videos?.demo && (
              <a href={tool.videos.demo} target="_blank" rel="noreferrer" className="video-link-btn">
                <span className="video-link-icon">📚</span>
                <span>Documentation</span>
              </a>
            )}
            {tool.website && (
              <a href={tool.website} target="_blank" rel="noreferrer" className="video-link-btn">
                <span className="video-link-icon">🌐</span>
                <span>Official Website</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

// ─── TOOL CARD ─────────────────────────────────────────────────────────────────
function ToolCard({ tool, onOpenVideo }) {
  const normalizeFeatureItem = (item) => {
    if (typeof item === "string") return { text: item, videoUrl: "" };
    return {
      text: item?.text || item?.title || "",
      videoUrl: item?.videoUrl || item?.video || "",
    };
  };

  return (
    <div className="tool-card">
      <div className="tool-header">
        <div className="tool-header-left">
          <div className="tool-icon">{tool.icon}</div>
          <div className="tool-title-group">
            <h3 className="tool-name">{tool.name}</h3>
            <p className="tool-company">{tool.company}</p>
          </div>
        </div>
        <div className="tool-badges">
           {(tool.badges || []).map((badge) => (
           <span key={badge} className={`badge ${badge}`}>{badge}</span>
           ))}
        </div>

      </div>

      <p className="tool-description">{tool.description}</p>

      {tool.newFeatures && (
        <div className="tool-new-features">
          <div className="new-features-title">
            <span className="new-features-icon">✨</span>
            New Features
          </div>
          <ul className="feature-list">
            {tool.newFeatures.features.map((feature, i) => {
              const featureData = normalizeFeatureItem(feature);
              return (
              <li key={i} className="feature-item">
                <span className="feature-text">
                  {featureData.text}
                  {featureData.videoUrl && (
                    <a
                      href={featureData.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="feature-link video"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Watch Video
                    </a>
                  )}
                  {!featureData.videoUrl && tool.videos && (
                    <a
                      className="feature-link video"
                      onClick={(e) => { e.stopPropagation(); onOpenVideo(tool); }}
                      style={{ cursor: "pointer" }}
                    >
                      Watch Video
                    </a>
                  )}
                  {!featureData.videoUrl && !tool.videos && tool.website && (
                    <a
                      href={tool.website}
                      target="_blank"
                      rel="noreferrer"
                      className="feature-link website"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Check Out the link
                    </a>
                  )}
                </span>
              </li>
            );
            })}
          </ul>
        </div>
      )}

      <div className="tool-tags">
        {tool.tags.slice(0, 3).map((tag) => (
          <span key={tag} className="tag">{tag}</span>
        ))}
      </div>

      <div className="tool-footer">
        <div className="tool-footer-left">
          <div className="tool-pricing">{tool.price}</div>
          <div className="tool-rating">
            <StarRating rating={tool.rating} />
            <span className="rating-count">({tool.reviews.toLocaleString()})</span>
          </div>
        </div>
        <a
          href={tool.website || "#"}
          target="_blank"
          rel="noreferrer"
          className="signup-btn"
          onClick={(e) => e.stopPropagation()}
        >
          Sign Up →
        </a>
      </div>
    </div>
  );
}

// ─── FILTER SECTION ────────────────────────────────────────────────────────────
function FilterSection({ title, options, filterKey, activeFilters, onChange }) {
  return (
    <div className="filter-section">
      <h3 className="filter-title">{title}</h3>
      {options.map((opt) => (
        <div key={opt.value} className="filter-option">
          <input
            type="checkbox"
            id={`${filterKey}-${opt.value}`}
            checked={activeFilters[filterKey].includes(opt.value)}
            onChange={(e) => onChange(filterKey, opt.value, e.target.checked)}
          />
          <label htmlFor={`${filterKey}-${opt.value}`}>{opt.label}</label>
          {/* <span className="filter-count">{opt.count}</span> */}
        </div>
      ))}
    </div>
  );
}

// ─── DIRECTORY PAGE ────────────────────────────────────────────────────────────
function DirectoryPage({ onOpenVideo, aiTools, categories }) {
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const [activeFilters, setActiveFilters] = useState({
    category: [], pricing: [], feature: [],
  });

  const debouncedSearch = useDebounce(searchInput, 300);

  const handleFilterChange = useCallback((filterKey, value, checked) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterKey]: checked
        ? [...prev[filterKey], value]
        : prev[filterKey].filter((v) => v !== value),
    }));
  }, []);

  const clearAll = () => {
    setSearchInput("");
    setSortBy("featured");
    setActiveFilters({ category: [], pricing: [], feature: [] });
  };

  const filteredTools = useMemo(() => {
    let tools = aiTools.filter((tool) => {
      if (activeFilters.category.length > 0 && !activeFilters.category.includes(tool.category)) return false;
      if (activeFilters.pricing.length > 0 && !activeFilters.pricing.includes(tool.pricing)) return false;
      if (activeFilters.feature.length > 0 && !activeFilters.feature.some((f) => tool.features.includes(f))) return false;
      if (debouncedSearch) {
        const searchable = `${tool.name} ${tool.company} ${tool.description} ${tool.tags.join(" ")}`.toLowerCase();
        if (!searchable.includes(debouncedSearch.toLowerCase())) return false;
      }
      return true;
    });

    switch (sortBy) {
      case "name":     return [...tools].sort((a, b) => a.name.localeCompare(b.name));
      case "rating":   return [...tools].sort((a, b) => b.rating - a.rating);
      case "popular":  return [...tools].sort((a, b) => b.reviews - a.reviews);
      case "newest":   return [...tools].filter(t => t.badges.includes("new")).concat(tools.filter(t => !t.badges.includes("new")));
      default:         return [...tools].filter(t => t.badges.includes("featured")).concat(tools.filter(t => !t.badges.includes("featured")));
    }
  }, [aiTools, activeFilters, debouncedSearch, sortBy]);

  return (
    <div id="directoryPage" className="page active">
      <div className="container">
        <main>
          {/* Sidebar */}
          <aside className="filters">
            <FilterSection
              title="Categories" options={categories}
              filterKey="category" activeFilters={activeFilters} onChange={handleFilterChange}
            />
            {/* <FilterSection
              title="Pricing" options={PRICING_OPTIONS}
              filterKey="pricing" activeFilters={activeFilters} onChange={handleFilterChange}
            />
            <FilterSection
              title="Features" options={FEATURE_OPTIONS}
              filterKey="feature" activeFilters={activeFilters} onChange={handleFilterChange}
            /> */}
            <button className="clear-filters" onClick={clearAll}>Clear All Filters</button>
          </aside>

          {/* Tools Grid */}
          <section className="tools-section">
            <div className="results-header">
              <div className="results-count">
                Showing <strong id="resultCount">{filteredTools.length}</strong> tools
              </div>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="featured">Featured</option>
                <option value="name">Name (A-Z)</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest First</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>

            <div className="tools-grid" id="toolsGrid">
              {filteredTools.length === 0 ? (
                <div className="no-results" style={{ gridColumn: "1 / -1" }}>
                  <h3>No tools found</h3>
                  <p>Try adjusting your filters or search query</p>
                </div>
              ) : (
                filteredTools.map((tool) => (
                  <ToolCard key={tool.name} tool={tool} onOpenVideo={onOpenVideo} />
                ))
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

// ─── FEATURES PAGE ─────────────────────────────────────────────────────────────
function FeaturesPage({ onOpenVideo, aiTools }) {
  const normalizeFeatureItem = (item) => {
    if (typeof item === "string") return { text: item, videoUrl: "" };
    return {
      text: item?.text || item?.title || "",
      videoUrl: item?.videoUrl || item?.video || "",
    };
  };

  const toolsWithFeatures = useMemo(() =>
    [...aiTools]
      .filter((t) => t.newFeatures)
      .sort((a, b) => new Date(b.newFeatures.releaseDate) - new Date(a.newFeatures.releaseDate)),
    [aiTools]
  );

  return (
    <div id="featuresPage" className="page active">
      <div className="container">
        <div className="features-page-header">
          <h1>✨ Latest AI Features & Updates</h1>
          <p>Stay ahead with the newest capabilities from top AI tools</p>
        </div>

        <div id="featuresContent">
          <div className="features-grid">
            {toolsWithFeatures.map((tool) => (
              <div key={tool.name} className="feature-card">
                <div className="feature-card-header">
                  <div className="feature-tool-icon">{tool.icon}</div>
                  <div className="feature-tool-info">
                    <h3>{tool.name}</h3>
                    <p className="feature-tool-company">{tool.company}</p>
                    <div className="feature-release-date">
                      <span>🗓️</span>
                      Released: {tool.newFeatures.releaseDate}
                    </div>
                  </div>
                </div>
                <div className="feature-details">
                  <h4>Latest Updates:</h4>
                  <ul>
                    {tool.newFeatures.features.map((feature, i) => {
                      const featureData = normalizeFeatureItem(feature);
                      return (
                      <li key={i}>
                        {featureData.text}
                        {featureData.videoUrl && (
                          <a
                            className="feature-link video"
                            href={featureData.videoUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: "inline-block", marginLeft: "0.5rem" }}
                          >
                            Watch Video
                          </a>
                        )}
                        {!featureData.videoUrl && tool.videos && (
                          <a
                            className="feature-link video"
                            onClick={(e) => { e.preventDefault(); onOpenVideo(tool); }}
                            href="#"
                            style={{ display: "inline-block", marginLeft: "0.5rem" }}
                          >
                            Watch Video
                          </a>
                        )}
                        {!featureData.videoUrl && !tool.videos && tool.website && (
                          <a
                            href={tool.website}
                            target="_blank"
                            rel="noreferrer"
                            className="feature-link website"
                            style={{ display: "inline-block", marginLeft: "0.5rem" }}
                          >
                            Check Out the link
                          </a>
                        )}
                      </li>
                    );
                    })}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── APP ROOT ──────────────────────────────────────────────────────────────────
export default function App() {
  const [activePage, setActivePage] = useState("directory");
  const [videoModalTool, setVideoModalTool] = useState(null);
  const [homeDirectoryData, setHomeDirectoryData] = useState({
    categories: [],
    aiTools: [],
  });

  const openVideoModal  = useCallback((tool) => setVideoModalTool(tool), []);
  const closeVideoModal = useCallback(() => setVideoModalTool(null), []);

  useEffect(() => {
    let isMounted = true;

    const loadHomeDirectoryData = async () => {
      try {
        const response = await fetch(END_POINT.GetHomeDirectoryData);
        const result = await response.json();

        if (!isMounted || !result?.success || !result?.data) return;

        setHomeDirectoryData({
          categories: Array.isArray(result.data.categories)
            ? result.data.categories
            : [],
          aiTools: Array.isArray(result.data.aiTools)
            ? result.data.aiTools
            : [],
        });
      } catch (error) {
        console.log("Failed to load home directory data", error);
      }
    };

    loadHomeDirectoryData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      {/* Pass activePage + setActivePage to your Header component */}
      <Header
        activePage={activePage}
        onShowPage={setActivePage}
        toolCount={homeDirectoryData.aiTools.length}
      />


      {activePage === "directory" && (
        <DirectoryPage
          onOpenVideo={openVideoModal}
          aiTools={homeDirectoryData.aiTools}
          categories={homeDirectoryData.categories}
        />
      )}
      {activePage === "features" && (
        <FeaturesPage
          onOpenVideo={openVideoModal}
          aiTools={homeDirectoryData.aiTools}
        />
      )}

      {videoModalTool && (
        <VideoModal tool={videoModalTool} onClose={closeVideoModal} />
      )}
    </>
  );
}
